import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import type { ExamPaper, ExamsApi, TopicsDocument } from '../api/exams'

/**
 * An Exam's whole Topic list in one file, and the prompt that turns a published PDF into it.
 *
 * A specification is tens of Topics with a paragraph under each, typed by hand through a
 * drawer one at a time. Nobody was going to do that twice, so the document arrives the way it is
 * published -- as a document -- and the drawer and the grid stay for correcting it afterwards.
 *
 * JSON rather than the YAML the Question upload takes, for one reason: this parse happens in
 * the browser, `JSON.parse` is already there, and a YAML parser would be a dependency bought
 * to read a file an LLM writes either way. Two shapes are taken:
 *
 * - the API's own body -- the Topics with their Subtopics, and the Topic Codes each Paper (or
 *   Session) examines, exactly what `PUT /admin/exams/{code}/topics` takes;
 * - the specification JSON the authoring repository publishes, its Topics written inside each
 *   Paper and each Topic's Subtopics as the lines of its "detail" after the first. That is
 *   turned into the body here, before anything is checked or sent.
 */
export function TopicsUpload({
  exams,
  exam,
  components,
  held,
}: {
  exams: ExamsApi
  exam: string
  /** The Exam's own Components and their Sections, which the file names and the prompt lists. */
  components: ExamPaper[]
  /** How many Topics the Exam already has, so an upload over written work is refused before it is sent. */
  held: number
}) {
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [copied, setCopied] = useState(false)

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const parsed = read(await file.text(), components)
      if (typeof parsed === 'string') throw new Error(`${file.name}: ${parsed}`)

      if (held > 0) {
        throw new Error(
          `${exam} already has ${held} Topics. An upload writes the Topics whole, so it will not go over ones that are already written: edit them here instead.`,
        )
      }

      return exams.setTopics(exam, parsed)
    },
    onSuccess: (written) => client.setQueryData(['topics', exam], written),
  })

  const send = (list: FileList | null) => {
    const file = list?.[0]
    if (file) upload.mutate(file)
  }

  const written = prompt(components)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(written)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // The panel shows the prompt in full, so a refused clipboard leaves it there to select
      // by hand rather than unreachable. Nothing to report but the icon not changing.
    }
  }

  return (
    <section
      aria-label="Upload the Topics"
      onDragOver={(event) => {
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault()
        setOver(false)
        send(event.dataTransfer.files)
      }}
      className={`rounded-lg border-2 border-dashed p-4 ${over ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={quiet} onClick={() => picker.current?.click()}>
          <Upload aria-hidden size={16} /> Upload the Topics
        </button>
        <input
          ref={picker}
          type="file"
          accept=".json,application/json"
          className="sr-only"
          aria-label="Topics file to upload"
          onChange={(event) => send(event.target.files)}
        />
        <span className="text-muted">or drop one .json file here</span>
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-muted">
          Converting a published PDF? Take the prompt.
        </summary>
        <div className="mt-2 flex items-start gap-2">
          <pre className="max-h-64 flex-1 overflow-auto rounded-md border border-ink-100 bg-page p-3 text-xs whitespace-pre-wrap">
            {written}
          </pre>
          {/* Named for what it does rather than labelled, because the panel is already narrow
              and the word Copy beside a copy icon is the icon said twice. */}
          <button
            type="button"
            onClick={copy}
            aria-label={copied ? 'Prompt copied' : 'Copy the prompt'}
            title={copied ? 'Copied' : 'Copy the prompt'}
            className="rounded-md p-2 text-green-800 hover:bg-green-050"
          >
            {copied ? <Check aria-hidden size={16} /> : <Copy aria-hidden size={16} />}
          </button>
        </div>
      </details>

      {upload.isPending && <p className="mt-3 text-muted">Reading the file...</p>}
      {upload.isError && (
        <p role="alert" className="mt-3 text-red-700">
          {upload.error.message}
        </p>
      )}
      {upload.isSuccess && (
        <p role="status" className="mt-3 font-bold">
          {upload.data.topics.length} Topics written across {upload.data.components.length} Components.
        </p>
      )}
    </section>
  )
}

/**
 * The API's own grammar for a Topic Code: a letter, then letters or digits, and optionally a
 * hyphen and another such run -- P2-Prf, where the part before the hyphen names the Paper.
 */
const CODE = /^[A-Za-z][A-Za-z0-9]*(?:-[A-Za-z][A-Za-z0-9]*)?$/

/** The API's limit on a Topic Code. */
const MAX_CODE = 10

/** The name the API gives the one Session of a Paper the Exam does not split. */
const DEFAULT_SESSION = 'Default'

/**
 * The file, checked before any of it is sent: either the document it describes, or the one
 * sentence saying what is wrong with it.
 *
 * Checked rather than merely parsed, because a file written by an LLM is the one input on this
 * screen nobody typed: a missing `code`, or a Subtopic written as an object rather than its
 * text, would otherwise reach the API as a body the contract does not describe and come back as
 * a validation error naming a field the Admin never saw.
 */
export function read(text: string, components: ExamPaper[]): TopicsDocument | string {
  let parsed: unknown

  try {
    parsed = JSON.parse(text)
  } catch {
    return 'not JSON. Copy the whole object the LLM wrote, without the fence around it.'
  }

  const body = (isSpecification(parsed) ? fromSpecification(parsed, components) : parsed) as {
    topics?: unknown
    components?: unknown
  }

  if (!Array.isArray(body.topics) || body.topics.length === 0) return 'no "topics" list.'
  if (!Array.isArray(body.components)) return 'no "components" list.'

  // Matched without regard to case, as the API matches a Topic Code.
  const codes = new Set<string>()

  for (const [at, entry] of body.topics.entries()) {
    const topic = entry as { code?: unknown; name?: unknown; detail?: unknown; subtopics?: unknown }
    const where = `topic ${at + 1}`

    if (typeof topic.code !== 'string' || topic.code.trim() === '') return `${where} has no "code".`
    if (!CODE.test(topic.code.trim()) || topic.code.trim().length > MAX_CODE) {
      return `${where}'s code "${topic.code}" is not a Topic Code: a letter, then letters or digits, with one hyphen at most, ten at most.`
    }
    if (typeof topic.name !== 'string' || topic.name.trim() === '') return `${where} has no "name".`
    if (codes.has(topic.code.trim().toLowerCase())) return `${where} repeats the code ${topic.code}.`
    codes.add(topic.code.trim().toLowerCase())

    const detail = optional(topic.detail, `${where}'s "detail"`, MAX_DETAIL)
    if (detail) return detail

    if (topic.subtopics !== undefined && topic.subtopics !== null) {
      if (!Array.isArray(topic.subtopics)) return `${where}'s "subtopics" is not a list.`

      for (const [which, line] of topic.subtopics.entries()) {
        const here = `subtopic ${which + 1} of ${topic.code}`

        if (typeof line !== 'string' || line.trim() === '') return `${here} is not text.`
        if (line.length > MAX_SUBTOPIC) {
          return `${here} runs to ${line.length} characters; the limit is ${MAX_SUBTOPIC}.`
        }
      }
    }
  }

  const named = new Set<string>()
  const placed = new Set<string>()

  for (const [which, sheet] of body.components.entries()) {
    const paper = sheet as { componentCode?: unknown; topics?: unknown; sections?: unknown }
    const here = `paper ${which + 1}`

    if (typeof paper.componentCode !== 'string' || paper.componentCode.trim() === '') {
      return `${here} has no "componentCode".`
    }

    // The Exam decides what its Components are; the API refuses a Style it does not set, and
    // saying so here names the ones it does rather than sending the file to find out.
    const set = components.find((entry) => entry.code === paper.componentCode)
    if (!set) {
      return `${here} is "${paper.componentCode}", which is not a Paper of this Exam. Its Components are: ${components.map((entry) => entry.code).join(', ')}.`
    }
    if (named.has(paper.componentCode)) return `${here} repeats the Paper ${paper.componentCode}.`
    named.add(paper.componentCode)

    // Sections where the Exam splits the Paper, its Topic Codes straight under it where it
    // does not -- the API reads that as the Default Session -- and never both.
    if (paper.topics !== undefined && paper.sections !== undefined) {
      return `${paper.componentCode} has both "topics" and "sections"; give one or the other.`
    }

    let sections: { name: string; topics: unknown }[]

    if (paper.sections === undefined) {
      if (!Array.isArray(paper.topics)) return `${paper.componentCode} has no "topics" list.`
      sections = [{ name: paper.componentCode, topics: paper.topics }]
    } else {
      if (!Array.isArray(paper.sections) || paper.sections.length === 0) {
        return `${paper.componentCode} has an empty or broken "sections" list.`
      }

      sections = []

      for (const [at, part] of paper.sections.entries()) {
        const session = part as { sectionCode?: unknown; topics?: unknown }
        const where = `session ${at + 1} of ${paper.componentCode}`

        if (typeof session.sectionCode !== 'string' || session.sectionCode.trim() === '') {
          return `${where} has no "sectionCode".`
        }
        if (!set.sections.some((sat) => sat.code === session.sectionCode)) {
          return `${where} is "${session.sectionCode}", which is not a Session of ${paper.componentCode}. Its Sections are: ${set.sections.map((sat) => sat.code).join(', ')}.`
        }
        if (!Array.isArray(session.topics)) return `${where} has no "topics" list.`

        sections.push({ name: `${paper.componentCode} ${session.sectionCode}`, topics: session.topics })
      }
    }

    for (const session of sections) {
      const listed = session.topics as unknown[]
      const seen = new Set<string>()

      // The rule the API holds, said here with the Session's name in front of it.
      if (listed.length === 0) return `${session.name} examines no Topic.`

      for (const [at, code] of listed.entries()) {
        const where = `entry ${at + 1} of ${session.name}`

        if (typeof code !== 'string' || !codes.has(code.trim().toLowerCase())) {
          return `${where} names ${JSON.stringify(code)}, which is not one of the file's "topics".`
        }
        if (seen.has(code.trim().toLowerCase())) return `${where} repeats the code ${code}.`
        seen.add(code.trim().toLowerCase())
        placed.add(code.trim().toLowerCase())
      }
    }
  }

  const missing = components.filter((paper) => !named.has(paper.code)).map((paper) => paper.code)
  if (missing.length > 0) return `no Topics for ${missing.join(', ')}; every Paper examines at least one.`

  const unplaced = (body.topics as { code: string }[]).find((topic) => !placed.has(topic.code.trim().toLowerCase()))
  if (unplaced) return `${unplaced.code} is under no Paper; every Topic sits under at least one.`

  return { topics: body.topics, components: body.components } as TopicsDocument
}

/**
 * Whether the file is the authoring repository's specification rather than the API's body: no
 * top-level "topics", and Components whose "topics" are Topics written out rather than codes.
 */
function isSpecification(parsed: unknown): parsed is { components: unknown[] } {
  const body = parsed as { topics?: unknown; components?: unknown } | null

  if (!body || typeof body !== 'object' || body.topics !== undefined || !Array.isArray(body.components)) return false

  return body.components.some((paper) => {
    const topics = (paper as { topics?: unknown } | null)?.topics
    return Array.isArray(topics) && topics.some((topic) => typeof topic === 'object' && topic !== null)
  })
}

/**
 * The specification as the API's body: every Paper's Topics in order, each with its Subtopics
 * taken from the lines of its "detail" after the first -- the first is the heading's preamble,
 * and is dropped rather than kept as the detail -- and each Paper naming its Topics by code.
 *
 * A Paper the Exam splits into Sections has every Session examine every one of its Topics,
 * since the specification writes Topics per Paper and nothing finer. What is not the shape
 * expected is passed through as it stands, for `read` to name.
 */
export function fromSpecification(
  specification: { components: unknown[] },
  components: ExamPaper[],
): { topics: unknown[]; components: unknown[] } {
  const topics: unknown[] = []

  const written = specification.components.map((sheet) => {
    const paper = sheet as { componentCode?: unknown; topics?: unknown }
    const listed = Array.isArray(paper.topics) ? paper.topics : []

    const codes = listed.map((entry) => {
      const topic = entry as { code?: unknown; name?: unknown; detail?: unknown }
      const lines = typeof topic.detail === 'string' ? topic.detail.split(/\r?\n/) : []
      const subtopics = lines
        .slice(1)
        .map((line) => line.trim())
        .filter((line) => line !== '')

      topics.push({ code: topic.code, name: topic.name, subtopics })
      return topic.code
    })

    const set = components.find((entry) => entry.code === paper.componentCode)
    const split = set && (set.sections.length > 1 || set.sections.some((sat) => sat.name !== DEFAULT_SESSION))

    return split
      ? {
          componentCode: paper.componentCode,
          sections: set.sections.map((sat) => ({ sectionCode: sat.code, topics: codes })),
        }
      : { componentCode: paper.componentCode, topics: codes }
  })

  return { topics, components: written }
}

/** The API's own column width. Longer, and the write is a truncation. */
const MAX_DETAIL = 8000

/** The API's limit on one Subtopic's text. */
const MAX_SUBTOPIC = 1000

/**
 * What is wrong with an optional field, or null where nothing is.
 *
 * Absent is the ordinary answer -- an entry the document writes as a bare label has no
 * wording to carry -- so only a value of the wrong type or the wrong length is a fault. Length
 * is checked here rather than left to the database, because a Detail that runs long is the
 * failure an LLM actually produces and a truncation is not an error the Admin can read.
 */
function optional(value: unknown, what: string, limit: number): string | null {
  if (value === undefined || value === null) return null
  if (typeof value !== 'string') return `${what} is not text.`
  if (value.length > limit) {
    return `${what} runs to ${value.length} characters; the limit is ${limit}.`
  }
  return null
}

/**
 * What to give an LLM along with the published specification, for this Exam.
 *
 * IT NAMES THE EXAM'S PAPERS AND SESSIONS. A specification says "Paper 1"; what is stored is
 * the Style the Format gives that Paper, and no rule turns one into the other -- so the codes
 * go into the prompt with the Format's own titles beside them, and the model matches rather
 * than invents. The Sections are the Format's too, named as it names them.
 *
 * Written as extraction and not as interpretation, for the same reason the Question extraction
 * is: a specification is the document an Exam is examined against, so a Topic reworded on its
 * way in is a Topic list that no longer says what was published.
 *
 * NOTHING IS WEIGHED. A Mock is composed outside the platform, so a Paper lists the Topics it
 * examines and nothing more.
 *
 * THE SUBTOPICS ARE THE POINT. A stray Question is matched against what the specification
 * examines under a heading, so each content point is kept verbatim, in order, as one Subtopic.
 */
export function prompt(components: ExamPaper[]): string {
  return `You are converting a published exam syllabus (a content specification) into JSON.

This exam sets these papers, and every "componentCode" below must be one of these codes:
${components
  .map(
    (paper) =>
      `- ${paper.code}: ${paper.name}, ${paper.minutes} minutes, ${paper.questionCount} questions${
        paper.sections.length > 1
          ? `, sat as sessions named ${paper.sections.map((session) => `"${session.code}"`).join(', ')}`
          : ''
      }`,
  )
  .join('\n')}

I am attaching the specification. Read all of it, not only the topic list: the paper structure, the assumed knowledge, the notes and the exclusions.

Reply with one JSON object and nothing else -- no commentary, and no markdown fence around it:

{
  "topics": [
    {
      "code": "<the paper's code, a hyphen, then the topic's code as printed, e.g. P1-MM1>",
      "name": "<the topic's heading, verbatim>",
      "detail": "<the topic's own statement under its heading, verbatim, if it has one>",
      "subtopics": ["<the topic's first content point, verbatim>", "<the second>"]
    }
  ],
  "components": [
    {
      "componentCode": "<the code of a paper from the list above>",
      "topics": ["<the code of a topic this paper examines>"]
    }
  ]
}

A paper the list above says is sat as sessions gives "sections" INSTEAD OF "topics", one per session, named exactly as listed:

    {
      "componentCode": "<code>",
      "sections": [
        { "sectionCode": "<a section code from the list above>", "topics": [ ...topic codes, as above... ] }
      ]
    }

Rules:
- A TOPIC BELONGS TO ONE PAPER. Write each topic under the paper that examines it, its code starting with that paper's code and a hyphen: P1-MM1. A heading examined in two papers is two topics, P1-MM1 and P2-MM1, each listed by its own paper.
- "topics" lists every topic once, paper by paper, in the document's order.
- A topic's "name" is its heading, copied verbatim: do not paraphrase, expand an abbreviation or correct spelling.
- "subtopics" ARE THE TOPIC'S CONTENT POINTS: every numbered entry, bullet or sub-heading beneath the heading, one string each, copied verbatim with its notes, examples and exclusions, in the document's order. Keep the document's order: the first is the most central. Do not number them; their codes are given from the order. At most 1000 characters each.
- "detail" is the statement written under the heading before its content points, verbatim. Leave it out where there is none. At most 8000 characters.
- A CODE IS A LETTER FOLLOWED BY LETTERS OR DIGITS, then a hyphen and another such run, at most ten characters, with no spaces or dots. Use the code the document prints where it fits; where it does not, or a topic has no printed code, write the shortest code that does -- "1.1" becomes "T11" -- and list every code you made up on a line beginning "NOTE:" after the JSON. Every code is unique and a quoted string.
- "componentCode" IS A CODE FROM THE LIST ABOVE AND NOTHING ELSE. Match each of the document's papers to one of them by name and by order -- the document's "Paper 1" is the first in the list, and its title will usually be the same words. Never write the document's own wording here, never invent a code, and never use a code that is not listed. A paper's title, how long it runs and how many questions it sets are already known here and are NOT to be written into the JSON.
- UNDER EACH PAPER, OR EACH SESSION, LIST THE CODES OF THE TOPICS IT EXAMINES, and nothing else: no weights, percentages or counts. Every topic sits under at least one paper or session, and every paper and session lists at least one topic. Take which from wherever the document states it: a heading over a run of topics, a column, a sentence in the paper structure section. Never infer it.
- Keep the document's own order, papers included. Do not sort, merge or split anything.
- Include every paper and every topic the document lists, and nothing it does not: no inferred topics, no examples of your own, and no preamble, aims or assessment-objective sections unless the document lists them as topics.
- Leave mathematical notation as the document writes it, in plain text.`
}

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'
